'use client';

import { c as _c } from "react/compiler-runtime";
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTime';
import useAuth from '@shared/utils/hooks/useAuth';
import { useConversationActions } from '@shared/utils/hooks/useConversations';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, Clock, Shield, Users } from 'lucide-react';
import { useState } from 'react';
import { animDelay } from '../../utils/animDelay';
import { client, type RouterOutputs, service } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import { useChatWorkspace } from '../pages/chat/ChatWorkspaceContext';
const formatDate = (dateString: Date) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
type BanStatsButtonProps = {
  spaceSlug: string;
  count: number;
};
const BanStatsButton = t0 => {
  const $ = _c(10);
  const {
    spaceSlug,
    count
  } = t0;
  const [modalOpen, setModalOpen] = useState(false);
  const t1 = `Bans: ${count}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setModalOpen(true);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Shield size={16} />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1) {
    t4 = <Button text={t1} onClick={t2} variant="CLEAR" classNames="px-3 py-1" icon={t3} />;
    $[2] = t1;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== modalOpen || $[5] !== spaceSlug) {
    t5 = modalOpen && <BanStatsModal closeHandler={() => setModalOpen(false)} spaceSlug={spaceSlug} />;
    $[4] = modalOpen;
    $[5] = spaceSlug;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
type BanStatsModalProps = {
  closeHandler: () => void;
  spaceSlug: string;
};
const BanStatsModal = t0 => {
  const $ = _c(14);
  const {
    closeHandler,
    spaceSlug
  } = t0;
  let t1;
  if ($[0] !== spaceSlug) {
    t1 = {
      queryKey: ["banStats", spaceSlug],
      queryFn: async () => {
        const [error, result] = await client.spaces.banStats.query({
          slug: spaceSlug
        });
        if (error) {
          throw error;
        }
        return result;
      }
    };
    $[0] = spaceSlug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: "800px",
      width: "90vw"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="anim-enter flex items-center gap-3 [--enter-y:-10px]"><Shield className="size-5 text-white" /><div><h2 className="text-2xl font-semibold text-textPrimary">Ban Statistics</h2></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== closeHandler) {
    t4 = <ModalHeader closeHandler={closeHandler}>{t3}</ModalHeader>;
    $[4] = closeHandler;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== data || $[7] !== isLoading || $[8] !== spaceSlug) {
    t5 = <ModalContent>{isLoading ? <div className="flex flex-col items-center justify-center p-12"><div className="size-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /><p className="anim-enter mt-4 text-textSecondary [--enter-delay:500ms]">Loading ban statistics...</p></div> : <div className="anim-enter space-y-6"><div className="grid grid-cols-1 gap-4 md:grid-cols-3"><div className="anim-enter rounded-xl border border-ds-border-standard bg-background-secondary p-4 [--enter-ease:var(--ease-spring-soft)] [--enter-scale:0.9]"><div className="flex items-center gap-3"><div className="rounded-lg bg-primary/20 p-2"><AlertTriangle className="size-5 text-primary" /></div><div><p className="text-2xl font-semibold text-textPrimary">{data?.totalBans || 0}</p><p className="text-sm text-textSecondary">Total Bans</p></div></div></div><div style={animDelay(200)} className="anim-enter rounded-xl border border-ds-border-standard bg-background-secondary p-4 [--enter-ease:var(--ease-spring-soft)] [--enter-scale:0.9]"><div className="flex items-center gap-3"><div className="rounded-lg bg-primary/20 p-2"><Clock className="size-5 text-primary" /></div><div><p className="text-2xl font-semibold text-textPrimary">{data?.otherBans?.banCount || 0}</p><p className="text-sm text-textSecondary">Other Bans</p></div></div></div><div style={animDelay(100)} className="anim-enter rounded-xl border border-ds-border-standard bg-background-secondary p-4 [--enter-ease:var(--ease-spring-soft)] [--enter-scale:0.9]"><div className="flex items-center gap-3"><div className="rounded-lg bg-primary/20 p-2"><Users className="size-5 text-primary" /></div><div><p className="text-2xl font-semibold text-textPrimary">{data?.ruleBanCounts?.length || 0}</p><p className="text-sm text-textSecondary">Rules</p></div></div></div></div><div className="space-y-4"><h3 className="anim-enter flex items-center gap-2 text-lg font-semibold text-textPrimary [--enter-x:-20px]"><Shield className="size-5 text-primary" />Ban Breakdown by Rules</h3><div className="space-y-3">{data?.ruleBanCounts?.map((item, index) => <div key={item.rule.id} style={animDelay(index * 100)} className="anim-enter [--enter-y:20px]"><RuleAccordion ruleData={item} spaceSlug={spaceSlug} /></div>)}{data?.otherBans?.banCount !== undefined && data.otherBans.banCount > 0 && <div style={animDelay((data?.ruleBanCounts?.length || 0) * 100)} className="anim-enter [--enter-y:20px]"><RuleAccordion ruleData={{
                banCount: data.otherBans.banCount,
                recentBans: data.otherBans.recentBans
              }} spaceSlug={spaceSlug} /></div>}{!data?.ruleBanCounts?.length && !data?.otherBans?.banCount && <div className="anim-enter py-12 text-center"><div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/10"><Shield className="size-8 text-green-500" /></div><h4 className="mb-2 text-lg font-emphasis text-textPrimary">No Bans Yet</h4><p className="text-textSecondary">This space has a clean record with no bans issued.</p></div>}</div></div></div>}</ModalContent>;
    $[6] = data;
    $[7] = isLoading;
    $[8] = spaceSlug;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== closeHandler || $[11] !== t4 || $[12] !== t5) {
    t6 = <Modal closeHandler={closeHandler} style={t2}>{t4}{t5}</Modal>;
    $[10] = closeHandler;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
type BanDisputeButtonProps = {
  ban: RouterOutputs['spaces']['banStats']['ruleBanCounts'][number]['recentBans'][number];
  spaceSlug: string;
};
const BanDisputeButton = t0 => {
  const $ = _c(13);
  const {
    ban,
    spaceSlug
  } = t0;
  const {
    currentUser
  } = useUser();
  const {
    addConversations
  } = useConversationActions();
  const {
    openChat
  } = useChatWorkspace();
  const [isCreating, setIsCreating] = useState(false);
  const hasExistingDispute = Boolean(ban.conversationId);
  const isBannedUser = ban.user.id === currentUser.id;
  if (!hasExistingDispute && !isBannedUser) {
    return null;
  }
  let t1;
  if ($[0] !== addConversations || $[1] !== openChat || $[2] !== spaceSlug) {
    t1 = async () => {
      setIsCreating(true);
      const [error, result] = await client.messages.createBanDispute.mutate({
        spaceSlug
      });
      if (error) {
        return;
      }
      if (result?.id) {
        addConversations([result]);
        openChat(result.id);
      }
      setIsCreating(false);
    };
    $[0] = addConversations;
    $[1] = openChat;
    $[2] = spaceSlug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleOpenDispute = t1;
  let t2;
  if ($[4] !== addConversations || $[5] !== ban.conversationId || $[6] !== openChat) {
    t2 = async () => {
      if (ban.conversationId) {
        const [error_0, conversationData] = await client.messages.getById.query({
          conversationId: ban.conversationId
        });
        if (error_0) {
          return;
        }
        if (conversationData) {
          addConversations([conversationData]);
        }
        openChat(ban.conversationId);
      }
    };
    $[4] = addConversations;
    $[5] = ban.conversationId;
    $[6] = openChat;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleViewDiscussion = t2;
  const t3 = hasExistingDispute ? handleViewDiscussion : handleOpenDispute;
  const t4 = `rounded-lg px-3 
        py-1.5 text-xs font-emphasis transition-all duration-200 hover:scale-[1.05] active:scale-[0.95]
        ${hasExistingDispute ? "bg-primary text-white hover:bg-indigo-400" : "bg-gradient-to-r from-orange-500 to-destructive text-white hover:from-orange-600 hover:to-destructive/90"}
        shadow-sm hover:shadow-md
        disabled:cursor-not-allowed disabled:opacity-50
      `;
  const t5 = isCreating ? "Creating..." : hasExistingDispute ? "View Discussion" : "Open Dispute";
  let t6;
  if ($[8] !== isCreating || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <button onClick={t3} disabled={isCreating} className={t4}>{t5}</button>;
    $[8] = isCreating;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
type RuleAccordionProps = {
  ruleData: RouterOutputs['spaces']['banStats']['ruleBanCounts'][number] | RouterOutputs['spaces']['banStats']['otherBans'];
  spaceSlug: string;
};
const RuleAccordion = t0 => {
  const $ = _c(41);
  const {
    ruleData,
    spaceSlug
  } = t0;
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      client,
      service
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    currentUser
  } = useAuth(t1);
  let t2;
  if ($[1] !== expanded) {
    t2 = () => setExpanded(!expanded);
    $[1] = expanded;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10"><Shield className="size-4 text-primary" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = "rule" in ruleData ? ruleData.rule.title : "Other Violations";
  const t5 = "inherited" in ruleData && ruleData.inherited && " (sitewide)";
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = <span className="font-semibold text-textPrimary">{t4}{t5}</span>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = ruleData.banCount === 1 ? "ban" : "bans";
  let t8;
  if ($[7] !== ruleData.banCount || $[8] !== t7) {
    t8 = <span className="text-sm text-textSecondary">{ruleData.banCount} {t7}</span>;
    $[7] = ruleData.banCount;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== ruleData.banCount) {
    t9 = ruleData.banCount > 0 && <div className="size-2 rounded-full bg-destructive" />;
    $[10] = ruleData.banCount;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <div className="anim-enter mt-1 flex items-center gap-2 [--enter-delay:100ms]">{t8}{t9}</div>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t6) {
    t11 = <div className="flex items-center gap-3">{t3}<div>{t6}{t10}</div></div>;
    $[15] = t10;
    $[16] = t6;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = `p-1 transition-transform duration-300 ${expanded ? "rotate-180" : "rotate-0"}`;
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronDown size={20} className="text-textSecondary" />;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12) {
    t14 = <div className={t12}>{t13}</div>;
    $[19] = t12;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t14 || $[23] !== t2) {
    t15 = <div className="flex cursor-pointer items-center justify-between p-4 transition-all duration-200 hover:bg-background-primary/50 active:scale-[0.98]" onClick={t2}>{t11}{t14}</div>;
    $[21] = t11;
    $[22] = t14;
    $[23] = t2;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = `anim-collapse ${expanded ? "anim-collapse-open" : ""}`;
  let t17;
  if ($[25] !== ruleData) {
    t17 = "rule" in ruleData && ruleData.rule.description && <div className="anim-enter mb-4 rounded-lg bg-background-secondary/50 p-3 [--enter-y:-10px]"><p className="text-sm italic text-textSecondary">"{ruleData.rule.description}"</p></div>;
    $[25] = ruleData;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-textPrimary"><Users className="size-4" />Recent Banned Users</h4>;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== currentUser || $[29] !== ruleData.recentBans || $[30] !== spaceSlug) {
    t19 = ruleData.recentBans.length === 0 ? <div className="anim-enter py-6 text-center"><div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-green-500/10"><Shield className="size-6 text-green-500" /></div><p className="text-sm text-textSecondary">No recent bans for this rule</p></div> : <div className="space-y-3">{ruleData.recentBans.map((ban, index) => <div key={ban.id} style={animDelay(index * 50)} className="anim-enter flex items-center justify-between rounded-lg bg-background-secondary/50 p-3 transition-all duration-200 [--enter-x:-10px] hover:scale-[1.01] hover:bg-background-light"><div className="flex items-center gap-3"><div className="transition-transform duration-200 hover:scale-[1.05]"><Avatar avatarKey={ban.user.avatarKey} robohash={ban.user.robohash} isBot={ban.user.tag === "BOT"} size={40} className="ring-2 ring-primary/20" userId={ban.user.id} /></div><div><p className="font-emphasis text-textPrimary">@{ban.user.username}</p><p className="text-xs text-textSecondary">{formatRelativeTime(ban.createdAt)}</p></div></div><div className="flex items-center gap-3"><div className="text-right"><div className="text-xs text-textSecondary"><div className="flex items-center gap-1"><Clock className="size-3" />Expires: {formatDate(ban.expiresAt)}</div></div></div>{currentUser.isAuthenticated && <BanDisputeButton ban={ban} spaceSlug={spaceSlug} />}</div></div>)}</div>;
    $[28] = currentUser;
    $[29] = ruleData.recentBans;
    $[30] = spaceSlug;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t17 || $[33] !== t19) {
    t20 = <div><div className="border-t border-ds-border-subtle bg-background-primary/30 p-4">{t17}{t18}{t19}</div></div>;
    $[32] = t17;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t16 || $[36] !== t20) {
    t21 = <div className={t16}>{t20}</div>;
    $[35] = t16;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t15 || $[39] !== t21) {
    t22 = <div className="overflow-hidden rounded-xl border border-ds-border-subtle bg-background-secondary/50 backdrop-blur-sm transition-all duration-200 hover:border-primary/30 hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)]">{t15}{t21}</div>;
    $[38] = t15;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  return t22;
};
export default BanStatsButton;